import React, { useEffect, useRef } from 'react';

import { ProcedurePills, Spacer } from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { Request } from '@/data/Requests';
import {
  ATTENDANCE_STATUS_OPTIONS,
  GENDER_LABELS,
  OFF_DUTY_PATIENT_ID
} from '@/utils/constants';
import { formatMobileNumber } from '@/utils/helpers/formatMobileNumber';
import { formatName } from '@/utils/helpers/formatName';
import { now } from '@/utils/helpers/now';
import { parseDate } from '@/utils/helpers/parseDate';
import { parseTime } from '@/utils/helpers/parseTime';
import { GenderLabelKeys } from '@/utils/types';
import { differenceInYears, format, isBefore, isEqual } from 'date-fns';
import {
  alertCircleOutline,
  bodyOutline,
  callOutline,
  createOutline,
  maleFemaleOutline
} from 'ionicons/icons';

import './TimelineItem.css';

interface TimelineItemProps {
  request: Request;
  onClick: (request: Request, isPast?: boolean) => void;
  isLast: boolean;
  isDifferentTime: boolean;
  isOverlappedStartTime: boolean;
  focusedRequestId?: string;
  showEditProcedures?: boolean;
  onEditProcedures?: (request: Request) => void;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
  request,
  onClick,
  isLast,
  isDifferentTime,
  isOverlappedStartTime,
  focusedRequestId,
  showEditProcedures,
  onEditProcedures
}) => {
  const timelineItemRef = useRef<HTMLDivElement>(null);

  const formatTime = (time: string | null) => format(parseTime(time), 'h:mm a');

  const showDetails =
    request.patient?.gender ||
    request.patient?.birthday ||
    request.patient?.mobile_number;

  const isPast =
    isBefore(parseDate(`${request.date}T${request.end_time}`), now()) ||
    isEqual(parseDate(`${request.date}T${request.end_time}`), now());

  const showAttendanceStatus =
    isPast &&
    request.attendance_status &&
    request.attendance_status !== ATTENDANCE_STATUS_OPTIONS.DEFAULT;

  const isOffDuty = request.patient_id === OFF_DUTY_PATIENT_ID;

  useEffect(() => {
    if (focusedRequestId === request.id) {
      timelineItemRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [focusedRequestId, request.id, timelineItemRef.current]);

  const showEndTime = isLast || isDifferentTime;

  return (
    <div
      className={`timeline-item ${isPast ? 'timeline-item--past' : ''} ${!isLast ? 'timeline-item--connects-next' : ''}`}
      ref={timelineItemRef}
    >
      <div className="timeline-item__time">
        <div
          className={`timeline-item__circle ${isOverlappedStartTime ? 'timeline-item__circle--overlapped-start-time' : ''}`}
        >
          <p>{formatTime(request.start_time)}</p>
        </div>
        <div className="timeline-item__line"></div>
        {showEndTime && (
          <div className="timeline-item__circle timeline-item__circle--end-time">
            <p>{formatTime(request.end_time)}</p>
          </div>
        )}
      </div>
      <div
        className="timeline-item__content"
        onClick={() => onClick(request, isPast)}
      >
        <div className="timeline-item__content-header">
          <p className="timeline-item__patient">
            {formatName(request.patient?.first_name)}
            {isOffDuty ? '-' : ' '}
            <Text color="primary">
              {formatName(request.patient?.last_name)}
            </Text>
          </p>
          {showEditProcedures && onEditProcedures && (
            <button
              type="button"
              className="timeline-item__edit-procedures"
              aria-label="Edit Procedures"
              onClick={(e) => {
                e.stopPropagation();
                onEditProcedures(request);
              }}
            >
              <Icon icon={createOutline} />
            </button>
          )}
        </div>
        <Spacer top={8} bottom={showDetails ? 8 : 0}>
          <ProcedurePills procedures={request.purpose?.split(',') || []} />
        </Spacer>
        {showDetails && (
          <div className="timeline-item__details">
            {request.patient?.gender && (
              <p>
                <Icon icon={maleFemaleOutline} />{' '}
                {GENDER_LABELS[
                  request.patient.gender.toUpperCase() satisfies GenderLabelKeys
                ] ?? GENDER_LABELS.OTHER}
              </p>
            )}
            {request.patient?.birthday && (
              <p>
                <Icon icon={bodyOutline} />{' '}
                {differenceInYears(
                  now(),
                  parseDate(request.patient.birthday)
                )}{' '}
                years old
              </p>
            )}
            {request.patient?.mobile_number && (
              <p>
                <Icon icon={callOutline} />
                {formatMobileNumber(request.patient.mobile_number)}
              </p>
            )}
          </div>
        )}
        {showAttendanceStatus && (
          <p className="timeline-item__attendance-status">
            <Icon icon={alertCircleOutline} />
            {request.attendance_status?.toUpperCase()}
          </p>
        )}
      </div>
    </div>
  );
};

export default TimelineItem;
